import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Combobox from "../../../shared/ui/Field/Combobox";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Textarea from "../../../shared/ui/Field/Textarea";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import Text from "../../../shared/ui/Typography/Text";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import { createClient } from "../api/clients";
import { searchCompanies } from "../api/companies";
import { createJoinRequest } from "../api/joinRequests";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// JoinRequestWithClientForm — feature widget. Owns ALL local state
// for the bounded dependent-resource flow:
//
//   (optional) create Client  →  create CompanyJoinRequest
//
// No Redux. No useReducer. No entity adapter. No normalized store.
// No cache. No React Query / RTK Query. No orchestration engine.
// No useDependentResource / useCreateAndAttach / useCoordinator.
// No compensating rollback if the second POST fails — partial
// persistence is the contract (see migration/phase15-dependent-resource-plan.md §6).
//
// The "dependency" is TEMPORAL, not STRUCTURAL: the join-request
// body does NOT reference the new client server-side. The chain is
// just two sequential POSTs in one async handler.

// ============================================================
// CONSTANTS (Step 3)
// ============================================================

const DEBOUNCE_MS = 200;
const MESSAGE_MAX = 1000;
const FIRST_NAME_MAX = 128;
const LAST_NAME_MAX = 128;
const PHONE_MAX = 32;
const NOTES_MAX = 2000;

const CLIENT_EDITABLE_FIELDS = [
  "first_name",
  "last_name",
  "phone",
  "notes",
  "type",
];

const JOIN_REQUEST_EDITABLE_FIELDS = ["message"];

const INITIAL_CLIENT_DRAFT = {
  first_name: "",
  last_name: "",
  phone: "",
  notes: "",
};

const INITIAL_VALIDATION_ERRORS = {
  client: {},
  joinRequest: {},
};

// ============================================================
// VALIDATION (Step 6)
// Feature-local, explicit. No schema engine. No registry.
// ============================================================

const validateClientDraft = (draft) => {
  const errors = {};

  const firstName = (draft.first_name ?? "").trim();
  if (firstName.length === 0) {
    errors.first_name = "Введіть ім'я.";
  } else if (firstName.length > FIRST_NAME_MAX) {
    errors.first_name = `Не довше ${FIRST_NAME_MAX} символів.`;
  }

  const lastName = draft.last_name ?? "";
  if (lastName.length > LAST_NAME_MAX) {
    errors.last_name = `Не довше ${LAST_NAME_MAX} символів.`;
  }

  const phone = (draft.phone ?? "").trim();
  if (phone.length === 0) {
    errors.phone = "Введіть телефон.";
  } else if (phone.length > PHONE_MAX) {
    errors.phone = `Не довше ${PHONE_MAX} символів.`;
  }

  const notes = draft.notes ?? "";
  if (notes.length > NOTES_MAX) {
    errors.notes = `Не довше ${NOTES_MAX} символів.`;
  }

  return errors;
};

const validateMessage = (msg) => {
  if ((msg ?? "").length > MESSAGE_MAX) {
    return `Не довше ${MESSAGE_MAX} символів.`;
  }
  return undefined;
};

// ============================================================
// HELPERS
// ============================================================

const toOption = (company) => ({
  id: company.id,
  label: company.name,
  sublabel: company.city || undefined,
  raw: company,
});

const toClientBody = (draft) => {
  const body = {
    first_name: draft.first_name.trim(),
    phone: draft.phone.trim(),
    type: "buyer", // hidden default — see plan §17 question 2
  };
  const lastName = (draft.last_name ?? "").trim();
  if (lastName.length > 0) body.last_name = lastName;
  const notes = draft.notes ?? "";
  if (notes.length > 0) body.notes = notes;
  return body;
};

const clientSummaryLabel = (client) => {
  if (!client) return "";
  const first = client.first_name || "";
  const last = client.last_name || "";
  const name = `${first} ${last}`.trim();
  if (name.length > 0) return name;
  return client.phone || `#${client.id}`;
};

// ============================================================
// COMPONENT (Steps 3–11)
// ============================================================

function JoinRequestWithClientForm() {
  // ----- Company picker state (Step 4) -----
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // ----- Client draft state (Step 5) -----
  const [includeClient, setIncludeClient] = useState(false);
  const [clientDraft, setClientDraft] = useState(INITIAL_CLIENT_DRAFT);
  const [createdClient, setCreatedClient] = useState(null);

  // ----- Per-resource async state (Step 9) -----
  const [createClientState, setCreateClientState] = useState("idle");
  const [joinRequestState, setJoinRequestState] = useState("idle");

  // ----- Validation / error routing (Step 6) -----
  const [validationErrors, setValidationErrors] = useState(
    INITIAL_VALIDATION_ERRORS,
  );

  // ----- Join-request body -----
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  // ----- Combobox async coordination refs -----
  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  // ============================================================
  // COMBOBOX LIFECYCLE (Step 4 — debounce + latest-wins)
  // Mirrors Phase 10 / Phase 11 lifecycle. Feature-local; no
  // shared search state, no cache, no background sync.
  // ============================================================
  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length === 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      latestRequestIdRef.current += 1;
      setOptions([]);
      setSearching(false);
      setSearchError(null);
      return undefined;
    }

    setSearching(true);
    setSearchError(null);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      searchCompanies(trimmed)
        .then((data) => {
          if (latestRequestIdRef.current !== requestId) return;
          const results = Array.isArray(data?.results) ? data.results : [];
          setOptions(results.map(toOption));
          setSearching(false);
        })
        .catch((err) => {
          if (latestRequestIdRef.current !== requestId) return;
          setSearchError(messageFromError(err));
          setOptions([]);
          setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [query]);

  // Final unmount cleanup — invalidate in-flight responses.
  useEffect(() => {
    return () => {
      latestRequestIdRef.current += 1;
    };
  }, []);

  // ============================================================
  // DERIVED STATE
  // ============================================================
  const isBusy =
    createClientState === "saving" || joinRequestState === "saving";

  const companyAcceptsJoin = Boolean(
    selectedCompany && selectedCompany.is_open_to_join,
  );

  const showCompanyClosedWarning = Boolean(
    selectedCompany && !selectedCompany.is_open_to_join,
  );

  const showSelectionHint = Boolean(
    selectedCompany && query !== (selectedCompany.name ?? ""),
  );

  // The client section's typed values are considered "pending" if
  // the toggle is on AND we have not yet succeeded at creating one.
  // Once `createdClient` is set, that part of the chain is done and
  // a re-submit MUST skip the client POST — see Step 7.
  const clientPending = includeClient && !createdClient;

  const canSubmit =
    !isBusy
    && companyAcceptsJoin
    && confirmed;

  // ============================================================
  // FIELD HANDLERS — clear targeted errors on change
  // ============================================================
  const handleQueryChange = useCallback((event) => {
    setQuery(event.target.value);
    setJoinRequestState((prev) => (prev === "error" ? "idle" : prev));
  }, []);

  const handleSelect = useCallback((option) => {
    if (!option || !option.raw) return;
    setSelectedCompany(option.raw);
    setQuery(option.raw.name ?? "");
    setValidationErrors((prev) => ({
      ...prev,
      joinRequest: {},
    }));
    setJoinRequestState((prev) => (prev === "error" ? "idle" : prev));
  }, []);

  const handleIncludeClientChange = useCallback((event) => {
    const next = event.target.checked;
    setIncludeClient(next);
    // Toggling OFF clears any client errors but PRESERVES the
    // typed values (plan §10) so toggling back ON restores them.
    if (!next) {
      setValidationErrors((prev) => ({ ...prev, client: {} }));
    }
  }, []);

  const handleClientFieldChange = useCallback((name) => (event) => {
    const value = event.target.value;
    setClientDraft((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => {
      const clientNext = { ...prev.client };
      delete clientNext[name];
      delete clientNext._form;
      return { ...prev, client: clientNext };
    });
    // Editing the client draft AFTER a successful POST is a no-op
    // for the chain — the next submit still skips client creation
    // because createdClient is set. We do NOT clear createdClient
    // on edits — that would force a duplicate POST and almost
    // certainly trip the unique_client_per_owner_phone constraint.
  }, []);

  const handleMessageChange = useCallback((event) => {
    setMessage(event.target.value);
    setValidationErrors((prev) => ({
      ...prev,
      joinRequest: {},
    }));
    setJoinRequestState((prev) => (prev === "error" ? "idle" : prev));
  }, []);

  const handleConfirmedChange = useCallback((event) => {
    setConfirmed(event.target.checked);
    setValidationErrors((prev) => ({
      ...prev,
      joinRequest: {},
    }));
    setJoinRequestState((prev) => (prev === "error" ? "idle" : prev));
  }, []);

  // ============================================================
  // RESET — used by "Send another request" + Cancel
  // ============================================================
  const resetAll = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    latestRequestIdRef.current += 1;
    setQuery("");
    setOptions([]);
    setSearching(false);
    setSearchError(null);
    setSelectedCompany(null);
    setIncludeClient(false);
    setClientDraft(INITIAL_CLIENT_DRAFT);
    setCreatedClient(null);
    setCreateClientState("idle");
    setJoinRequestState("idle");
    setValidationErrors(INITIAL_VALIDATION_ERRORS);
    setMessage("");
    setConfirmed(false);
  }, []);

  // ============================================================
  // SUBMIT — Steps 7 + 8 + 9
  // Sequential pessimistic chain. NOT Promise.all, NOT
  // Promise.allSettled, NOT a coordinator. Two awaits, top to
  // bottom. The `if (!createdClient)` short-circuit is what makes
  // partial-persistence retries cheap (plan §6).
  // ============================================================
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSubmit) return;

    // ----- (1) Local validation. Skip the client draft check
    //          if the toggle is off OR we already have a client.
    const nextErrors = { client: {}, joinRequest: {} };
    if (clientPending) {
      nextErrors.client = validateClientDraft(clientDraft);
    }
    const messageErr = validateMessage(message);
    if (messageErr) nextErrors.joinRequest.message = messageErr;
    if (
      Object.keys(nextErrors.client).length > 0
      || Object.keys(nextErrors.joinRequest).length > 0
    ) {
      setValidationErrors(nextErrors);
      return;
    }
    setValidationErrors(INITIAL_VALIDATION_ERRORS);

    // ----- (2) Client POST. ONLY runs if the user opted in AND
    //          we have not already persisted one. The
    //          short-circuit is the load-bearing partial-
    //          persistence retry mechanism.
    let client = createdClient;
    if (clientPending) {
      setCreateClientState("saving");
      try {
        client = await createClient(toClientBody(clientDraft));
        setCreatedClient(client);
        setCreateClientState("saved");
      } catch (err) {
        const parsed = parseDrfFieldErrors(err, CLIENT_EDITABLE_FIELDS);
        setValidationErrors((prev) => ({ ...prev, client: parsed }));
        setCreateClientState("error");
        return; // Do NOT proceed. The join request is NOT attempted.
      }
    }

    // ----- (3) Join request POST. Runs after client succeeded OR
    //          when the user did not opt in. Server-side coupling
    //          is ZERO — only sequencing.
    setJoinRequestState("saving");
    try {
      const body = message.trim().length > 0 ? { message: message.trim() } : {};
      await createJoinRequest(selectedCompany.id, body);
      setJoinRequestState("success");
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, JOIN_REQUEST_EDITABLE_FIELDS);
      setValidationErrors((prev) => ({ ...prev, joinRequest: parsed }));
      setJoinRequestState("error");
      // Critical: `createdClient` STAYS SET. Retry skips client.
      // No compensating delete. No rollback. Frontend is not a
      // transaction coordinator (brief — "CRITICAL PARTIAL-
      // PERSISTENCE RULE").
    }
  };

  // ============================================================
  // SUCCESS SURFACE — Step 10 (terminal sibling render branch)
  // ============================================================
  if (joinRequestState === "success") {
    const company = selectedCompany;
    const clientLabel = clientSummaryLabel(createdClient);
    const description = company
      ? createdClient
        ? `Адміністратор компанії «${company.name}» побачить ваш запит. Клієнт ${clientLabel} додано до вашого списку.`
        : `Адміністратор компанії «${company.name}» побачить ваш запит.`
      : "Запит надіслано.";
    return (
      <div
        className="feature-company-join-request-client__success"
        data-feature="company-join-request-client-success"
      >
        <EmptyState
          icon="fa-solid fa-circle-check"
          title="Запит надіслано"
          description={description}
          size="md"
        />
        <div className="feature-company-join-request-client__success-actions">
          <button
            type="button"
            className="ui-form__button ui-form__button--ghost"
            onClick={resetAll}
          >
            Надіслати ще один запит
          </button>
          <Link
            to="/companies/find"
            className="feature-company-join-request-client__back-link"
          >
            Повернутися до пошуку
          </Link>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER (Step 11)
  // ============================================================
  const trimmedQuery = query.trim();
  const emptyResultsLabel =
    trimmedQuery.length > 0
      ? `Жодних компаній за запитом «${trimmedQuery}» не знайдено.`
      : undefined;

  const clientFormError = validationErrors.client._form;
  const joinFormError = validationErrors.joinRequest._form;
  const messageError = validationErrors.joinRequest.message;

  const showClientSuccessStrip = Boolean(createdClient);

  return (
    <form
      className="ui-form"
      data-feature="company-join-request-client-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {/* ====================================================
          SECTION 1 — Company picker
          ==================================================== */}
      <section
        className="feature-company-join-request-client__section"
        data-section="company"
      >
        <h2 className="feature-company-join-request-client__section-heading">
          Компанія
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Знайдіть компанію за назвою, описом або містом.
        </Text>

        <div className="ui-form__rows">
          <Field
            id="join-request-client-company"
            label="Компанія"
            help="Виберіть зі списку запропонованих компаній."
            required
          >
            <Combobox
              name="company"
              value={query}
              onChange={handleQueryChange}
              options={options}
              loading={searching}
              error={searchError}
              emptyHint="Введіть назву компанії для пошуку."
              emptyResultsLabel={emptyResultsLabel}
              onSelect={handleSelect}
              placeholder="Назва, опис або місто…"
              autoComplete="off"
              disabled={isBusy}
            />
          </Field>

          {showSelectionHint ? (
            <Text
              as="div"
              size="xs"
              tone="secondary"
              className="feature-company-join-request-client__selection-hint"
            >
              Вибрано: {selectedCompany.name}
            </Text>
          ) : null}

          {showCompanyClosedWarning ? (
            <InlineError className="feature-company-join-request-client__warning">
              Ця компанія не приймає запити на приєднання.
            </InlineError>
          ) : null}
        </div>
      </section>

      {/* ====================================================
          SECTION 2 — Optional inline client creation
          ==================================================== */}
      <section
        className="feature-company-join-request-client__section"
        data-section="client"
      >
        <h2 className="feature-company-join-request-client__section-heading">
          Новий клієнт (необов'язково)
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Якщо ви хочете одразу додати першого клієнта до свого списку
          — заповніть базові дані. Запит буде надіслано після створення
          клієнта.
        </Text>

        <div className="feature-company-join-request-client__include">
          <Checkbox
            id="join-request-client-include"
            name="include_client"
            label="Додати нового клієнта разом із запитом"
            checked={includeClient}
            onChange={handleIncludeClientChange}
            disabled={isBusy || Boolean(createdClient)}
          />
        </div>

        {/* Persisted client confirmation strip — shown after a
            successful client POST. Stays visible even if the join
            request then fails (partial-persistence contract). */}
        {showClientSuccessStrip ? (
          <div
            className="feature-company-join-request-client__client-strip"
            data-state="saved"
          >
            <span
              className="feature-company-join-request-client__client-strip-icon"
              aria-hidden="true"
            >
              <i className="fa-solid fa-circle-check"></i>
            </span>
            <span className="feature-company-join-request-client__client-strip-text">
              Клієнт <strong>{clientSummaryLabel(createdClient)}</strong>
              {" "}створений.
              {createdClient.phone ? ` Телефон: ${createdClient.phone}.` : ""}
            </span>
          </div>
        ) : null}

        {/* Client draft form — only when toggle is on AND not yet
            created. Once `createdClient` is set, the strip replaces
            this surface. */}
        {includeClient && !createdClient ? (
          <div className="ui-form__rows">
            <div className="feature-company-join-request-client__pair">
              <Field
                id="join-request-client-first-name"
                label="Ім'я"
                error={validationErrors.client.first_name}
                required
              >
                <TextInput
                  name="first_name"
                  type="text"
                  value={clientDraft.first_name}
                  onChange={handleClientFieldChange("first_name")}
                  maxLength={FIRST_NAME_MAX}
                  autoComplete="given-name"
                  disabled={isBusy}
                />
              </Field>

              <Field
                id="join-request-client-last-name"
                label="Прізвище"
                error={validationErrors.client.last_name}
              >
                <TextInput
                  name="last_name"
                  type="text"
                  value={clientDraft.last_name}
                  onChange={handleClientFieldChange("last_name")}
                  maxLength={LAST_NAME_MAX}
                  autoComplete="family-name"
                  disabled={isBusy}
                />
              </Field>
            </div>

            <Field
              id="join-request-client-phone"
              label="Телефон"
              help="Унікальний у межах вашого облікового запису."
              error={validationErrors.client.phone}
              required
            >
              <TextInput
                name="phone"
                type="tel"
                value={clientDraft.phone}
                onChange={handleClientFieldChange("phone")}
                maxLength={PHONE_MAX}
                autoComplete="tel"
                placeholder="+380 …"
                disabled={isBusy}
              />
            </Field>

            <Field
              id="join-request-client-notes"
              label="Нотатки"
              help={`До ${NOTES_MAX} символів. Простий текст (необов'язково).`}
              error={validationErrors.client.notes}
            >
              <Textarea
                name="notes"
                value={clientDraft.notes}
                onChange={handleClientFieldChange("notes")}
                rows={4}
                maxLength={NOTES_MAX}
                placeholder="Особисті нотатки про клієнта…"
                disabled={isBusy}
              />
            </Field>

            {clientFormError ? (
              <InlineError className="feature-company-join-request-client__message">
                {clientFormError}
              </InlineError>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ====================================================
          SECTION 3 — Message + confirmation
          ==================================================== */}
      <section
        className="feature-company-join-request-client__section"
        data-section="request"
      >
        <h2 className="feature-company-join-request-client__section-heading">
          Запит
        </h2>
        <Text as="p" size="sm" tone="secondary">
          Коротко напишіть, чому ви хочете приєднатися (необов'язково).
        </Text>

        <div className="ui-form__rows">
          <Field
            id="join-request-client-message"
            label="Повідомлення"
            error={messageError}
          >
            <TextInput
              name="message"
              type="text"
              value={message}
              onChange={handleMessageChange}
              placeholder="Наприклад: маю досвід у комерційній нерухомості…"
              maxLength={MESSAGE_MAX}
              autoComplete="off"
              disabled={isBusy}
            />
          </Field>

          <div className="feature-company-join-request-client__confirm">
            <Checkbox
              id="join-request-client-confirm"
              name="confirmed"
              label="Підтверджую: компанія побачить моє ім'я, контакти та цей запит."
              checked={confirmed}
              onChange={handleConfirmedChange}
              disabled={isBusy || !companyAcceptsJoin}
            />
          </div>
        </div>

        <div className="ui-form__messages">
          {joinFormError ? (
            <InlineError className="ui-form__message">
              {joinFormError}
            </InlineError>
          ) : null}
          {createdClient && joinRequestState === "error" ? (
            <Text
              as="span"
              size="xs"
              tone="secondary"
              className="feature-company-join-request-client__retry-hint"
            >
              Клієнта вже створено. Натисніть «Надіслати запит» ще раз,
              щоб надіслати лише запит на приєднання.
            </Text>
          ) : null}
        </div>
      </section>

      {/* ====================================================
          ACTIONS
          ==================================================== */}
      <div className="ui-form__actions">
        <button
          type="button"
          className="ui-form__button ui-form__button--ghost"
          onClick={resetAll}
          disabled={isBusy}
        >
          Скасувати
        </button>
        <button
          type="submit"
          className="ui-form__button ui-form__button--primary"
          disabled={!canSubmit}
          data-loading={isBusy ? "true" : undefined}
        >
          {isBusy ? (
            <Spinner size="sm" label="Надсилання" />
          ) : (
            "Надіслати запит"
          )}
        </button>
      </div>
    </form>
  );
}

export default JoinRequestWithClientForm;
